import Autosuggest from 'react-autosuggest';
import PropTypes from 'prop-types';
import React from 'react';

import getStateNameFromCode from '../../services/getStateNameFromCode';

/**
 * Displays a search field to find entries in a given dataset.
 */
export default class SearchField extends React.PureComponent {
  /**
   * @param {string} str - User input
   * @returns {string} sanitized user input.
   */
  static escapeRegexCharacters(str) {
    return str.replace(/(Rep\.\s|Sen\.\s)/g, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // eslint-disable-line max-len
  }

  /**
   * Needed for Autosuggest component.
   * @returns {bool} returns true if called.
   */
  static shouldRenderSuggestions() {
    return true;
  }

  constructor(props) {
    super(props);

    this.state = {
      noSuggestions: false,
      suggestions: [],
      value: '',
    };

    this.onChange = this.onChange.bind(this);

    this.getSuggestions = this.getSuggestions.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.onSuggestionsFetchRequested = this
      .onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this
      .onSuggestionsClearRequested.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
  }

  onChange(event, input) {
    this.setState({
      value: input.newValue,
    });
  }

  onSuggestionsClearRequested() {
    this.setState({
      noSuggestions: false,
      suggestions: [],
    });
  }

  onSuggestionsFetchRequested(input) {
    const suggestions = this.getSuggestions(input.value);
    const isInputBlank = '' === input.value.trim();
    const noSuggestions = ! isInputBlank && 0 === suggestions.length;
    this.setState({
      suggestions,
      noSuggestions,
    });
  }

  /**
   * Needed for Autosuggest component. Attempts to match suggestions to input.
   * @param {string} input the entered value
   * @returns {string} the array of suggestions
   */
  getSuggestions(input) {
    const { data, dataType } = this.props;
    const escapedInput = SearchField
      .escapeRegexCharacters(input.trim());
    const regex = new RegExp(escapedInput, 'i');
    if ('members' === dataType) {
      const suggestions = data.filter((item) =>
        regex.test(item.firstName) ||
        regex.test(item.lastName) ||
        regex.test(`${item.firstName} ${item.lastName}`) ||
        regex.test(item.state) ||
        regex.test(getStateNameFromCode(item.state))
      );
      return suggestions;
    }
    const suggestions = data.filter((item) =>
      regex.test(item.name)
    );
    return suggestions;
  }

  /**
   * Needed for Autosuggest component. Gets the value to display in the search bar.
   * @param {object} suggestion - the suggestion object
   * @returns {string} the suggestion.
   */
  getSuggestionValue(suggestion) {
    const { dataType } = this.props;
    const value = 'members' === dataType ?
      `${('House' === suggestion.chamber) ? 'Rep.' : 'Sen.'} ${suggestion.firstName} ${suggestion.lastName}` :  // eslint-disable-line max-len
      suggestion.name;
    return value;
  }

  /**
   * Renders a suggestion.
   * @param {object} suggestion - suggestion object.
   * @returns {string} marked up result containing text.
   */
  renderSuggestion(suggestion) {
    const { dataType } = this.props;
    const { chamber, firstName, lastName, name, party, state } = suggestion;
    const isMember = 'members' === dataType;
    const label = isMember ?
      `${('House' === chamber) ? 'Rep.' : 'Sen.'} ${firstName} ${lastName}` :
      name;
    return (
      <div className="search-field--suggestion-container">
        <span className="search-field--suggestion-name">{label}</span>
        {isMember && (
          <span className="search-field--suggestion-partystate">
            {`${party}-${state}`}
          </span>
        )}
      </div>
    );
  }

  /**
   * Renders the component.
   */
  render() {
    const {
      noSuggestions,
      suggestions,
      value,
    } = this.state;

    const {
      dataType,
      onSelect,
    } = this.props;

    const isMember = 'members' === dataType;

    const inputProps = {
      placeholder: `Find ${isMember ? 'member of Congress' : 'company'}...`,
      value,
      onChange: this.onChange,
    };

    return (
      <div className="search-field">
        <Autosuggest
          suggestions={suggestions}
          onSuggestionSelected={onSelect}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          shouldRenderSuggestions={SearchField.shouldRenderSuggestions}
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps}
        />
        {
          noSuggestions &&
            <div className="react-autosuggest--no-results">
              No matching results found.
            </div>
        }
        <div className="icon-search search-field--icon" />
      </div>
    );
  }
}

SearchField.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape).isRequired,
  dataType: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};
